import React, { useState, useEffect } from 'react';
import { Save, X, Plus, Trash2, FileText, User, DollarSign, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { quoteService } from '../../services/quoteService';
import { productService } from '../../services/productService';
import { clientService } from '../../services/clientService';
import { notificationService } from '../../services/notificationService';
import { QuoteFormData, QuoteDetail, Product, QuoteCondition } from '../../types/product';
import { Customer } from '../../types/customer';

interface QuoteFormProps {
  customer?: Customer | null;
  quoteId?: number | null;
  onSave: () => void;
  onCancel: () => void;
}

const QuoteForm = ({ customer = null, quoteId = null, onSave, onCancel }: QuoteFormProps) => {
  // Función para generar el texto de observaciones basado en el nombre de la empresa
  const generateObservations = (customerName?: string) => {
    const companyName = customerName || 'el cliente';
    return `Los precios unitarios son fijos por cada producto y no incluyen El Impuesto General a las Ventas (IGV). La cantidad final deberá ser confirmada por ${companyName}.`;
  };

  const [formData, setFormData] = useState<QuoteFormData>({
    customer: customer,
    quote_code: quoteService.generateQuoteCode(),
    validity_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    currency: 'USD',
    payment_terms: 'Crédito a 30 días',
    payment_type: 'credito',
    credit_days: 30,
    delivery_days: 5,
    delivery_terms: '5 días hábiles después de la orden de compra',
    observations: generateObservations(customer?.company),
    additionalConditions: [],  // Inicializar con array vacío
    items: []
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [requestData, setRequestData] = useState<string>('');

  useEffect(() => {
    loadProducts();
    loadCustomers();
  }, []);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      customer: customer,
      observations: generateObservations(customer?.company)
    }));
  }, [customer]);

  useEffect(() => {
    if (quoteId) {
      loadQuoteForEditing(quoteId);
    }
  }, [quoteId]);

  const loadQuoteForEditing = async (id: number) => {
    try {
      const quoteWithDetails = await quoteService.getQuoteWithDetails(id);
      if (quoteWithDetails) {
        let quoteCustomer: Customer | null = null;
        let quoteCode = '';
        let observations = '';
        let requestData = ''; // Para almacenar los datos de la solicitud web
        
        // Verificar si es una cotización pendiente por el formato del nombre
        const isPendingQuote = quoteWithDetails.name && quoteWithDetails.name.startsWith('Cotización Pendiente - ');
        
        // Si la cotización tiene un customer_id, intentar cargar el cliente desde la base de datos
        if (quoteWithDetails.customer_id && quoteWithDetails.customers) {
          quoteCustomer = quoteWithDetails.customers;
        } else {
          // Convertir el cliente de la cotización a formato Customer
          quoteCustomer = {
            id: 0, // No tenemos ID del cliente en la cotización
            name: quoteWithDetails.name,
            email: quoteWithDetails.email,
            phone: quoteWithDetails.phone || '',
            company: quoteWithDetails.company || '',
            ruc: '',
            address: '',
            created_at: quoteWithDetails.created_at || '',
            updated_at: quoteWithDetails.updated_at || ''
          };
        }

        // Usar la función auxiliar para parsear las condiciones adicionales
        const parsedData = quoteService.parseAdditionalConditionsFromMessage(quoteWithDetails.message);
        
        // Extraer datos de la solicitud web del mensaje completo
        const fullMessage = quoteWithDetails.message;
        const messageLines = fullMessage.split('\n\n');
        
        // Filtrar líneas que contienen datos de la solicitud web
        const requestDataLines = messageLines.filter(line => {
          const requestDataPatterns = [
            'Servicio de interés:',
            'Industria:',
            'RUC:',
            'Especificaciones:',
            'Urgencia:',
            'Mensaje de prueba adicional'
          ];
          return requestDataPatterns.some(pattern => line.includes(pattern));
        });
        
        requestData = requestDataLines.join('\n\n');
        
        if (isPendingQuote) {
          // Para cotizaciones pendientes, extraer el código del mensaje
          const messageParts = quoteWithDetails.message.split(' - ');
          quoteCode = messageParts[0] || quoteService.generateQuoteCode();
          
          // Si el código contiene la palabra "Cotización", eliminarla
          if (quoteCode.startsWith('Cotización ')) {
            quoteCode = quoteCode.replace('Cotización ', '');
          }
          
          observations = parsedData.observations;
        } else {
          // Para cotizaciones normales, extraer el código del mensaje
          const messageParts = quoteWithDetails.message.split(' - ');
          quoteCode = messageParts[0] || quoteService.generateQuoteCode();
          
          // Si el código contiene la palabra "Cotización", eliminarla
          if (quoteCode.startsWith('Cotización ')) {
            quoteCode = quoteCode.replace('Cotización ', '');
          }
          
          observations = parsedData.observations;
        }

        setFormData({
          customer: quoteCustomer,
          quote_code: quoteCode,
          validity_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          currency: 'USD',
          payment_terms: quoteWithDetails.payment_terms || 'Crédito a 30 días',
          payment_type: quoteWithDetails.payment_type || 'credito',
          credit_days: quoteWithDetails.credit_days || 30,
          delivery_days: quoteWithDetails.delivery_days || 5,
          delivery_terms: quoteWithDetails.delivery_terms || '5 días hábiles después de la orden de compra',
          observations: observations,
          additionalConditions: parsedData.additionalConditions,  // Cargar las condiciones parseadas
          items: quoteWithDetails.quote_details && quoteWithDetails.quote_details.length > 0
            ? quoteWithDetails.quote_details.map(detail => ({
                ...detail,
                quote_id: 0 // No necesitamos el quote_id en el formulario
              }))
            : []
        });
      }
    } catch (error) {
      console.error('Error al cargar cotización para edición:', error);
      notificationService.error('Error', 'No se pudo cargar la cotización para edición');
    }
  };

  const loadProducts = async () => {
    const allProducts = await productService.getProducts();
    setProducts(allProducts);
  };

  const loadCustomers = async () => {
    const allCustomers = await clientService.getCustomers();
    setCustomers(allCustomers);
  };

  const handleAddItem = () => {
    const newItem: QuoteDetail = {
      quote_id: 0, // Agregar campo requerido
      product_id: 0,
      quantity: 1,
      unit_price: 0,
      total_price: 0,
      notes: ''
    };
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const handleRemoveItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index: number, field: keyof QuoteDetail, value: string | number) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      const item = { ...newItems[index] };
      
      if (field === 'product_id') {
        const productId = parseInt(value as string);
        const product = products.find(p => p.id === productId);
        if (product) {
          item.product_id = productId;
          item.unit_price = product.price;
          item.total_price = item.quantity * product.price;
        }
      } else if (field === 'quantity') {
        const quantity = parseInt(value as string) || 1;
        item.quantity = quantity;
        item.total_price = quantity * item.unit_price;
      } else if (field === 'unit_price') {
        const unitPrice = parseFloat(value as string) || 0;
        item.unit_price = unitPrice;
        item.total_price = item.quantity * unitPrice;
      } else {
        // Para el campo notes, mantenemos el tipo string
        (item as any)[field] = value;
      }
      
      newItems[index] = item;
      return {
        ...prev,
        items: newItems
      };
    });
  };

  const calculateTotal = () => {
    return formData.items.reduce((total, item) => total + item.total_price, 0);
  };

  // Funciones para manejar condiciones adicionales
  const handleAddCondition = () => {
    if (formData.additionalConditions.length >= 5) {
      notificationService.warning('Límite alcanzado', 'Solo se pueden agregar un máximo de 5 condiciones adicionales');
      return;
    }

    const newCondition: QuoteCondition = {
      id: Date.now().toString(), // ID único basado en timestamp
      name: '',
      content: '',
      includeInPDF: true
    };

    setFormData(prev => ({
      ...prev,
      additionalConditions: [...prev.additionalConditions, newCondition]
    }));
  };

  const handleRemoveCondition = (conditionId: string) => {
    setFormData(prev => ({
      ...prev,
      additionalConditions: prev.additionalConditions.filter(condition => condition.id !== conditionId)
    }));
  };

  const handleConditionChange = (conditionId: string, field: keyof QuoteCondition, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      additionalConditions: prev.additionalConditions.map(condition =>
        condition.id === conditionId
          ? { ...condition, [field]: value }
          : condition
      )
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!customer && !formData.customer) {
      newErrors.customer = 'Debe seleccionar un cliente para la cotización';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'Debe agregar al menos un producto a la cotización';
    }

    formData.items.forEach((item, index) => {
      if (item.product_id === 0) {
        newErrors[`item_${index}_product`] = 'Debe seleccionar un producto';
      }
      if (item.quantity <= 0) {
        newErrors[`item_${index}_quantity`] = 'La cantidad debe ser mayor a 0';
      }
      if (item.unit_price <= 0) {
        newErrors[`item_${index}_price`] = 'El precio unitario debe ser mayor a 0';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      let success = false;
      
      if (quoteId) {
        // Modo edición
        success = await quoteService.updateQuoteWithDetails(quoteId, formData);
        if (success) {
          notificationService.success('Cotización actualizada', 'La cotización ha sido actualizada exitosamente.');
        } else {
          notificationService.error('Error al actualizar cotización', 'Por favor, verifique los datos e intente nuevamente.');
        }
      } else {
        // Modo creación
        success = await quoteService.createQuoteWithDetails(formData);
        if (success) {
          notificationService.success('Cotización creada exitosamente', 'La cotización ha sido registrada en el sistema.');
        } else {
          notificationService.error('Error al crear la cotización', 'Por favor, verifique los datos e intente nuevamente.');
        }
      }
      
      if (success) {
        // Actualizar cotizaciones que tienen productos a estado "Cotizadas"
        await quoteService.updateQuotesWithProductsToQuoted();
        onSave();
      }
    } catch (error) {
      console.error('Error al guardar cotización:', error);
      const action = quoteId ? 'actualizar' : 'crear';
      notificationService.error(`Error al ${action} cotización`, 'Ha ocurrido un error inesperado. Por favor, intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">
          {quoteId ? (
            // Si es una cotización pendiente, mostrar el título original
            formData.customer?.company && formData.quote_code === '' ?
              `Cotización Pendiente - ${formData.customer.company}` :
              `Editar Cotización - ${formData.quote_code}`
          ) :
            `Nueva Cotización - ${formData.quote_code}`
          }
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-6">
        {/* Información del Cliente */}
        <div className="mb-8 p-4 bg-gray-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <User className="w-5 h-5 mr-2 text-primary-600" />
            Información del Cliente
          </h3>
          
          {customer || formData.customer ? (
            // Mostrar datos del cliente cuando está preseleccionado o cargado
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Cliente
                </label>
                <input
                  type="text"
                  value={customer?.name || formData.customer?.name || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={customer?.email || formData.customer?.email || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={customer?.phone || formData.customer?.phone || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Empresa
                </label>
                <input
                  type="text"
                  value={customer?.company || formData.customer?.company || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                />
              </div>
            </div>
          ) : (
            // Permitir seleccionar un cliente cuando no está preseleccionado
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Seleccionar Cliente *
                </label>
               
                
                <select
                  value={(formData.customer as any)?.id || ''}
                  onChange={(e) => {
                    const selectedCustomer = customers.find(c => c.id === parseInt(e.target.value));
                    setFormData(prev => ({
                      ...prev,
                      customer: selectedCustomer || null,
                      observations: generateObservations(selectedCustomer?.company)
                    }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.customer ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Seleccionar un cliente</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} {customer.company ? `- ${customer.company}` : ''} {customer.ruc ? `(${customer.ruc})` : ''}
                    </option>
                  ))}
                </select>
                {errors.customer && (
                  <p className="mt-1 text-sm text-red-600">{errors.customer}</p>
                )}
              </div>
            </div>
          )}
    
          {/* Datos de la Solicitud Web (solo para cotizaciones pendientes) */}
          {requestData && (
            <div className="mb-8 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-yellow-600" />
                Datos de la Solicitud Web
              </h3>
              
              <div className="bg-white p-4 rounded-lg border border-yellow-300">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                  {requestData}
                </pre>
              </div>
              
              <p className="text-xs text-yellow-600 mt-2">
                <strong>Nota:</strong> Estos datos son informativos y no se incluyen en el PDF de la cotización.
              </p>
            </div>
          )}
        </div>

        {/* Productos */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-primary-600" />
              Productos a Cotizar
            </h3>
            <Button
              type="button"
              onClick={handleAddItem}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Agregar Producto
            </Button>
          </div>


          {/* Lista de productos agregados */}
          <div className="space-y-4">
            {formData.items.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Producto *
                    </label>
                    <select
                      value={item.product_id}
                      onChange={(e) => handleItemChange(index, 'product_id', parseInt(e.target.value))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors[`item_${index}_product`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value={0}>Seleccionar producto</option>
                      {products.map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name} - ${product.price.toFixed(2)}
                        </option>
                      ))}
                    </select>
                    {errors[`item_${index}_product`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`item_${index}_product`]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cantidad *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors[`item_${index}_quantity`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors[`item_${index}_quantity`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`item_${index}_quantity`]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Precio Unitario *
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors[`item_${index}_price`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors[`item_${index}_price`] && (
                      <p className="mt-1 text-sm text-red-600">{errors[`item_${index}_price`]}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subtotal
                    </label>
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100">
                      ${item.total_price.toFixed(2)}
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notas del producto
                  </label>
                  <textarea
                    value={item.notes || ''}
                    onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Notas adicionales sobre este producto..."
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleRemoveItem(index)}
                    className="text-red-600 hover:text-red-800 hover:border-red-300"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {errors.items && (
            <p className="mt-2 text-sm text-red-600">{errors.items}</p>
          )}
        </div>

        {/* Condiciones de la Oferta */}
        <div className="mb-8 p-4 bg-blue-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-primary-600" />
            Condiciones de la Oferta
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Código de Cotización
              </label>
              {formData.quote_code ? (
                // Para cotizaciones con código, mostrar como no editable
                <input
                  type="text"
                  value={formData.quote_code}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                />
              ) : (
                // Para cotizaciones sin código, permitir editar
                <input
                  type="text"
                  value={formData.quote_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, quote_code: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Validez de la Cotización
              </label>
              <input
                type="date"
                value={formData.validity_date}
                onChange={(e) => setFormData(prev => ({ ...prev, validity_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Moneda de Cotización
              </label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="USD" selected>Dólares Americanos (USD)</option>
                <option value="PEN">Soles (PEN)</option>
                <option value="EUR">Euros (EUR)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Condición de Pago
              </label>
              <select
                value={formData.payment_type}
                onChange={(e) => {
                  const paymentType = e.target.value as 'contado' | 'credito';
                  const creditDays = paymentType === 'contado' ? 0 : formData.credit_days || 30;
                  const paymentTerms = paymentType === 'contado' ? 'Contado' : `Crédito a ${creditDays} días`;
                  
                  setFormData(prev => ({
                    ...prev,
                    payment_type: paymentType,
                    credit_days: creditDays,
                    payment_terms: paymentTerms
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="contado">Contado</option>
                <option value="credito">Crédito</option>
              </select>
            </div>

            {formData.payment_type === 'credito' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Días de Crédito
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={formData.credit_days}
                  onChange={(e) => {
                    const creditDays = parseInt(e.target.value) || 30;
                    const paymentTerms = `Crédito a ${creditDays} días`;
                    
                    setFormData(prev => ({
                      ...prev,
                      credit_days: creditDays,
                      payment_terms: paymentTerms
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tiempo de Entrega (días hábiles)
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={formData.delivery_days}
                onChange={(e) => {
                  const deliveryDays = parseInt(e.target.value) || 5;
                  const deliveryTerms = `${deliveryDays} días hábiles después de la orden de compra`;
                  
                  setFormData(prev => ({
                    ...prev,
                    delivery_days: deliveryDays,
                    delivery_terms: deliveryTerms
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="md:col-span-2 lg:col-span-3">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Observaciones y Condiciones
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddCondition}
                  disabled={formData.additionalConditions.length >= 5}
                  className="text-xs bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Agregar Condición ({formData.additionalConditions.length}/5)
                </Button>
              </div>
              
              {/* Observación fija (siempre visible y en el PDF) */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">Observación Principal</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Siempre en PDF</span>
                </div>
                <p className="text-sm text-gray-700">
                  {generateObservations(formData.customer?.company)}
                </p>
              </div>
              
              {/* Condiciones adicionales */}
              <div className="space-y-3">
                {formData.additionalConditions.map((condition, index) => (
                  <div key={condition.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-700">Condición {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => handleConditionChange(condition.id, 'includeInPDF', !condition.includeInPDF)}
                          className={`flex items-center space-x-1 text-xs px-2 py-1 rounded transition-colors ${
                            condition.includeInPDF
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {condition.includeInPDF ? (
                            <>
                              <ToggleRight className="w-4 h-4" />
                              <span>En PDF</span>
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="w-4 h-4" />
                              <span>Solo en sistema</span>
                            </>
                          )}
                        </button>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemoveCondition(condition.id)}
                        className="text-red-600 hover:text-red-800 hover:border-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Nombre de la condición
                        </label>
                        <input
                          type="text"
                          value={condition.name}
                          onChange={(e) => handleConditionChange(condition.id, 'name', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Ej: Garantía, Entrega, etc."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Contenido
                        </label>
                        <textarea
                          value={condition.content}
                          onChange={(e) => handleConditionChange(condition.id, 'content', e.target.value)}
                          rows={2}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Descripción de la condición..."
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                {formData.additionalConditions.length === 0 && (
                  <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg">
                    <p className="text-sm text-gray-500">
                      No hay condiciones adicionales agregadas
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Haga clic en "Agregar Condición" para añadir condiciones personalizadas
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Resumen */}
        <div className="mb-8 p-4 bg-green-50 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <DollarSign className="w-5 h-5 mr-2 text-primary-600" />
            Resumen de Cotización
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total de Productos
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100">
                {formData.items.length}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total General
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-lg font-semibold">
                ${calculateTotal().toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        {/* Botones de Acción */}
        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            className="btn-primary"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {quoteId ? 'Actualizar Cotización' : 'Crear Cotización'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default QuoteForm;
export {};